/**
 * Ella Radio session lifecycle — WAITING while stage-2 build catches up,
 * stall detection, and auto-resume when reconcile appends tracks.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';
import { useMaestroStore } from '../store/maestroStore';
import { QueueController } from '../transport/QueueController';
import { countRadioRegistryUnplayedBeyondQueue, isRadioQueueSession } from './radioTrackRegistry';
import { isEllaRadioBuildPollActive } from './ellaRadioBuildPoll';

export const RADIO_STALL_WARN_MS = 45_000;
export const RADIO_STALL_FATAL_MS = 120_000;
export const RADIO_LOW_QUEUE_SEC = 20;
export const RADIO_BUILD_QUEUE_WAIT_MS = 90_000;
/** Bounded watchdog — queue exhausted + registryUnplayed>0 must issue play within N seconds. */
export const RADIO_FEED_NOW_WATCHDOG_MS = 8_000;

export type RadioSessionUiCallbacks = {
  onStallWarning?: () => void;
  onBuildFatal?: (message: string) => void;
};

let _waiting = false;
let _autoResumeArmed = false;
let _buildStatus: string | null = null;
let _playlistId: string | null = null;
let _lastProgressAtMs: number | null = null;
let _stallWarned = false;
let _lowQueueKickSent = false;
let _feedWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
let _feedObligationStartedAt = 0;
let _uiCallbacks: RadioSessionUiCallbacks = {};
const _listeners = new Set<() => void>();

function idleLocalRendererForRadioWaiting(): void {
  void import('../transport/TransportCommand').then(({ TransportCommand }) => {
    TransportCommand.pauseLocalAudioElement();
  });
  const store = useMaestroStore.getState();
  if (store.playbackState === 'playing' || store.playbackState === 'loading') {
    store.setPlaybackState('buffering');
  }
}

function syncConnectorRadioWaiting(active: boolean): void {
  void import('../lib/connector').then(({ setRadioSessionWaiting }) => {
    void setRadioSessionWaiting(active);
  });
}

function releaseConnectorRadioHold(): void {
  if (!isRadioWaiting() && !isRadioExpectingMore()) {
    syncConnectorRadioWaiting(false);
  }
}

function emitChange(): void {
  _listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function registerRadioSessionUiCallbacks(callbacks: RadioSessionUiCallbacks): void {
  _uiCallbacks = callbacks;
}

export function subscribeRadioSessionState(listener: () => void): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}

export function resetRadioSessionState(): void {
  _waiting = false;
  _autoResumeArmed = false;
  _buildStatus = null;
  _playlistId = null;
  _lastProgressAtMs = null;
  _stallWarned = false;
  _lowQueueKickSent = false;
  _uiCallbacks = {};
  clearRadioFeedWatchdog();
  syncConnectorRadioWaiting(false);
  emitChange();
}

function clearRadioFeedWatchdog(): void {
  if (_feedWatchdogTimer) {
    clearTimeout(_feedWatchdogTimer);
    _feedWatchdogTimer = null;
  }
}

function armRadioFeedWatchdog(): void {
  clearRadioFeedWatchdog();
  _feedObligationStartedAt = Date.now();
  _feedWatchdogTimer = setTimeout(() => {
    _feedWatchdogTimer = null;
    void onRadioFeedWatchdogExpiry();
  }, RADIO_FEED_NOW_WATCHDOG_MS);
}

async function onRadioFeedWatchdogExpiry(): Promise<void> {
  if (!isRadioWaiting() && !isRadioAutoResumeArmed()) return;

  const { TransportCommand } = await import('../transport/TransportCommand');
  if (TransportCommand.isPlayUriRecent(RADIO_FEED_NOW_WATCHDOG_MS + 1_000)) {
    diagnosticLog('info', 'js:radio', 'radio_feed_watchdog_clear', {
      reason: 'play_uri_landed',
      elapsedMs: Date.now() - _feedObligationStartedAt,
    });
    clearRadioWaiting();
    return;
  }

  const registryUnplayed = countRadioRegistryUnplayedBeyondQueue();
  if (registryUnplayed <= 0) return;

  diagnosticLog('warn', 'js:radio', 'radio_feed_watchdog_force', {
    registryUnplayed,
    elapsedMs: Date.now() - _feedObligationStartedAt,
  });
  await obligateRadioFeedNow('radio_waiting_watchdog');
}

/** Per-build poll start — keeps UI callbacks and waiting state. */
export function resetRadioSessionPollFlags(): void {
  _stallWarned = false;
  _lowQueueKickSent = false;
}

export function setRadioSessionBuildMeta(
  playlistId: string,
  buildStatus: string,
  buildLastProgressAt?: string | null,
  buildStartedAt?: string | null,
): void {
  _playlistId = playlistId;
  _buildStatus = buildStatus;
  const progressMs = Date.parse(String(buildLastProgressAt || ''));
  const startedMs = Date.parse(String(buildStartedAt || ''));
  if (Number.isFinite(progressMs)) {
    _lastProgressAtMs = progressMs;
  } else if (Number.isFinite(startedMs)) {
    _lastProgressAtMs = startedMs;
  }
}

export function getRadioSessionBuildStatus(): string | null {
  return _buildStatus;
}

export function isRadioExpectingMore(): boolean {
  if (!isRadioQueueSession()) return false;
  if (isEllaRadioBuildPollActive()) return true;
  return _buildStatus === 'building';
}

/** Poll reconcile owns ingestion — refill/reserve must not grow the queue. */
export function isRadioArcIngestionPending(): boolean {
  if (!isRadioQueueSession()) return false;
  if (_buildStatus === 'building') return true;
  return countRadioRegistryUnplayedBeyondQueue() > 0;
}

/** Same defer predicate used at track-end and _advance terminal paths. */
export function shouldDeferRadioQueueTerminal(): boolean {
  if (!isRadioQueueSession()) return false;
  return isRadioExpectingMore() || countRadioRegistryUnplayedBeyondQueue() > 0;
}

export function armRadioAutoResume(_source: string): void {
  _autoResumeArmed = true;
}

export function disarmRadioAutoResume(reason: string): void {
  if (!_autoResumeArmed) return;
  _autoResumeArmed = false;
  diagnosticLog('info', 'js:radio', 'radio_auto_resume_disarmed', {
    reason,
    playlistId: _playlistId,
    queueIndex: useMaestroStore.getState().queueIndex,
  });
}

export function isRadioAutoResumeArmed(): boolean {
  return _autoResumeArmed;
}

/** First queue index the user has not heard — never the current/seed index, never 0 after play. */
export function computeRadioAutoResumeIndex(
  queueIndex: number,
  queueLength: number,
): number | null {
  const targetIdx = queueIndex + 1;
  if (targetIdx >= queueLength) return null;
  if (targetIdx === 0 || targetIdx === queueIndex) return null;
  return targetIdx;
}

/** Arm auto-resume and leave browser/local renderer idle at queue end. */
export function beginRadioQueueEndWait(): void {
  armRadioAutoResume('queue_end');
  idleLocalRendererForRadioWaiting();
}

/**
 * Explicit feed-now handoff when defer_radio — flush stale registry markers,
 * reconcile enqueue, and auto-resume if tracks landed.
 */
export async function obligateRadioFeedNow(source: string): Promise<boolean> {
  const { clearStaleRadioEnqueuedIds } = await import('./radioTrackRegistry');
  const staleCleared = clearStaleRadioEnqueuedIds();
  const registryUnplayed = countRadioRegistryUnplayedBeyondQueue();
  const store = useMaestroStore.getState();

  diagnosticLog('info', 'js:radio', 'radio_feed_now_obligation', {
    source,
    registryUnplayed,
    staleEnqueuedCleared: staleCleared,
    queueIndex: store.queueIndex,
    queueLength: store.queue.length,
    buildStatus: _buildStatus,
  });

  const { flushEllaRadioPendingEnqueue } = await import('./ellaRadioBuildPoll');
  await flushEllaRadioPendingEnqueue();

  const refreshed = useMaestroStore.getState();
  if (refreshed.queueIndex + 1 < refreshed.queue.length) {
    return tryRadioAutoResume(`${source}_obligation`);
  }
  return false;
}

export function enterRadioWaiting(): void {
  syncConnectorRadioWaiting(true);
  armRadioAutoResume('waiting');
  idleLocalRendererForRadioWaiting();
  void obligateRadioFeedNow('radio_waiting');
  if (_waiting) {
    armRadioFeedWatchdog();
    return;
  }
  _waiting = true;
  diagnosticLog('info', 'js:radio', 'radio_waiting', {
    playlistId: _playlistId,
    buildStatus: _buildStatus,
    queueLength: useMaestroStore.getState().queue.length,
    queueIndex: useMaestroStore.getState().queueIndex,
    registryUnplayed: countRadioRegistryUnplayedBeyondQueue(),
  });
  armRadioFeedWatchdog();
  emitChange();
}

export function clearRadioWaiting(): void {
  clearRadioFeedWatchdog();
  if (!_waiting) {
    releaseConnectorRadioHold();
    return;
  }
  _waiting = false;
  releaseConnectorRadioHold();
  emitChange();
}

export function isRadioWaiting(): boolean {
  return _waiting;
}

export function markLowQueueKickSent(): boolean {
  if (_lowQueueKickSent) return false;
  _lowQueueKickSent = true;
  return true;
}

export function getRadioPlaylistId(): string | null {
  return _playlistId;
}

export async function waitForRadioBuildQueueGrowth(timeoutMs = RADIO_BUILD_QUEUE_WAIT_MS): Promise<boolean> {
  const started = Date.now();
  const initialLen = useMaestroStore.getState().queue.length;
  while (Date.now() - started < timeoutMs) {
    if (!isRadioExpectingMore()) return false;
    const state = useMaestroStore.getState();
    if (state.queue.length > initialLen || state.queueIndex + 1 < state.queue.length) {
      return true;
    }
    await new Promise((r) => setTimeout(r, 400));
  }
  const final = useMaestroStore.getState();
  return final.queueIndex + 1 < final.queue.length;
}

export function tryRadioAutoResume(source: string): boolean {
  if (!_autoResumeArmed) return false;
  if (!isRadioWaiting() && !isRadioExpectingMore()) return false;
  const store = useMaestroStore.getState();
  const fromIdx = store.queueIndex;
  const targetIdx = computeRadioAutoResumeIndex(fromIdx, store.queue.length);
  if (targetIdx == null) {
    if (fromIdx + 1 < store.queue.length) {
      diagnosticLog('info', 'js:radio', 'radio_auto_resume_skipped', {
        source,
        reason: 'would_repeat_current_or_seed',
        queueIndex: fromIdx,
        queueLength: store.queue.length,
      });
    }
    return false;
  }

  _autoResumeArmed = false;
  clearRadioWaiting();
  diagnosticLog('info', 'js:radio', 'radio_auto_resume', {
    source,
    playlistId: _playlistId,
    fromIdx,
    targetIdx,
    queueLength: store.queue.length,
  });
  store.setQueueIndex(targetIdx);
  void QueueController.playQueueTrack(targetIdx);
  return true;
}

export function notifyRadioTracksAppended(appended: number, source: string): void {
  if (appended <= 0) return;
  if (isRadioWaiting() || (
    isRadioExpectingMore()
    && useMaestroStore.getState().queueIndex >= useMaestroStore.getState().queue.length - 1
  )) {
    tryRadioAutoResume(source);
  }
}

export type StallCheckResult = 'ok' | 'warn' | 'fatal';

export function checkBuildStall(nowMs = Date.now()): StallCheckResult {
  if (_buildStatus !== 'building') return 'ok';
  if (_lastProgressAtMs == null) return 'ok';
  const frozenMs = nowMs - _lastProgressAtMs;
  if (frozenMs >= RADIO_STALL_FATAL_MS) return 'fatal';
  if (frozenMs >= RADIO_STALL_WARN_MS && !_stallWarned) return 'warn';
  return 'ok';
}

export function markRadioBuildFatal(): void {
  _buildStatus = 'error';
  _waiting = false;
  syncConnectorRadioWaiting(false);
  emitChange();
}

/** Arm connector hold before queue-end race (timer-based renderers). */
export function armRadioConnectorHold(): void {
  if (isRadioExpectingMore()) {
    syncConnectorRadioWaiting(true);
  }
}

export function releaseRadioConnectorHoldIfIdle(): void {
  releaseConnectorRadioHold();
}

export function handleBuildStallCheck(nowMs = Date.now()): StallCheckResult {
  const result = checkBuildStall(nowMs);
  if (result === 'warn') {
    _stallWarned = true;
    diagnosticLog('warn', 'js:radio', 'radio_stall', {
      playlistId: _playlistId,
      frozenSinceMs: _lastProgressAtMs,
      frozenMs: nowMs - (_lastProgressAtMs || nowMs),
      phase: 'warning',
    });
    _uiCallbacks.onStallWarning?.();
  }
  if (result === 'fatal') {
    markRadioBuildFatal();
    diagnosticLog('error', 'js:radio', 'radio_stall', {
      playlistId: _playlistId,
      frozenSinceMs: _lastProgressAtMs,
      frozenMs: nowMs - (_lastProgressAtMs || nowMs),
      phase: 'fatal',
    });
    _uiCallbacks.onBuildFatal?.(
      'Station build stalled. Tap retry to start a fresh build.',
    );
  }
  return result;
}
